import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Loader2, CheckCircle, XCircle, Upload, FileText, Camera, Home, User, ArrowRight, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { registerKitService } from '@/api/services/registerKitService';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';

// File upload constraints
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

const HotspotRegistrationStep2 = () => {
  const { theme } = useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // debounce spam-click
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fullName: '',
    governmentIdType: '',
    idNumber: '',
  });

  const [documentUrls, setDocumentUrls] = useState({
    idFrontUrl: '',
    idBackUrl: '',
    proofOfAddressUrl: '',
    selfieUrl: '',
  });

  // Check for registration session on load
  useEffect(() => {
    const registrationId = sessionStorage.getItem('registrationId');
    if (!registrationId) {
      showMessage('error', 'No registration session found. Redirecting...');
      setTimeout(() => navigate('/operator/registerKit'), 2000);
    }
  }, [navigate]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (docType: string, file: File | null) => {
    if (!file) return;

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return showMessage('error', t('operator.registerKit.step2.errors.invalidFileType', {
        defaultValue: 'Invalid file type. Please upload JPG, PNG, WebP, or PDF.',
      }));
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return showMessage('error', t('operator.registerKit.step2.errors.fileTooLarge', {
        defaultValue: 'File is too large. Maximum size is {{size}}MB.',
        size: MAX_FILE_SIZE_MB,
      }));
    }

    const registrationId = sessionStorage.getItem('registrationId');
    if (!registrationId) {
      showMessage('error', t('operator.registerKit.step2.errors.sessionLost', {
        defaultValue: 'Your registration session was lost. Redirecting...',
      }));
      setTimeout(() => navigate('/operator/registerKit'), 2000);
      return;
    }

    setUploadingDoc(docType);

    try {
      const docTypeMap: Record<string, 'id_front' | 'id_back' | 'proof_of_address' | 'selfie'> = {
        frontIdProof: 'id_front',
        backIdProof: 'id_back',
        proofOfAddress: 'proof_of_address',
        selfieWithId: 'selfie',
      };

      const response = await registerKitService.uploadDocument(
        registrationId,
        docTypeMap[docType],
        file
      );

      const urlMap: Record<string, keyof typeof documentUrls> = {
        frontIdProof: 'idFrontUrl',
        backIdProof: 'idBackUrl',
        proofOfAddress: 'proofOfAddressUrl',
        selfieWithId: 'selfieUrl',
      };

      setDocumentUrls(prev => ({ ...prev, [urlMap[docType]]: response.documentUrl }));
      showMessage('success', t('operator.registerKit.step2.docUploadSuccess', {
        defaultValue: '{{doc}} uploaded successfully',
        doc: labelMap[docType],
      }));
    } catch (error: unknown) {
      const axiosErr = error instanceof AxiosError ? error as AxiosError<{ message?: string }> : null;
      const statusCode = axiosErr?.response?.status;
      const apiMsg = axiosErr?.response?.data?.message;
      let errorMsg = apiMsg || t('operator.registerKit.step2.errors.uploadFailed', {
        defaultValue: 'Upload failed. Please try again.',
      });
      if (statusCode === 413) {
        errorMsg = t('operator.registerKit.step2.errors.fileTooLarge', {
          defaultValue: 'File is too large. Maximum size is {{size}}MB.',
          size: MAX_FILE_SIZE_MB,
        });
      } else if (statusCode === 401) {
        errorMsg = t('operator.registerKit.step2.errors.unauthorized', {
          defaultValue: 'Your session expired. Please sign in again.',
        });
        setTimeout(() => navigate('/signin'), 2000);
      } else if (!navigator.onLine) {
        errorMsg = t('operator.registerKit.step2.errors.offline', {
          defaultValue: 'You appear to be offline. Please check your connection.',
        });
      }
      showMessage('error', errorMsg);
    } finally {
      setUploadingDoc(null);
    }
  };

  const labelMap: Record<string, string> = {
    frontIdProof: t('operator.registerKit.step2.docs.idFront', { defaultValue: 'Front of ID' }),
    backIdProof: t('operator.registerKit.step2.docs.idBack', { defaultValue: 'Back of ID' }),
    proofOfAddress: t('operator.registerKit.step2.docs.proofOfAddress', { defaultValue: 'Proof of Address' }),
    selfieWithId: t('operator.registerKit.step2.docs.selfie', { defaultValue: 'Selfie with ID' }),
  };

  const handleSubmit = async () => {
    if (submitting || loading) return;

    if (!formData.fullName.trim() || !formData.governmentIdType || !formData.idNumber.trim()) {
      return showMessage('error', t('operator.registerKit.step2.errors.fieldsRequired', {
        defaultValue: 'Please complete all identity fields.',
      }));
    }

    const missingDocs = Object.values(documentUrls).some(url => !url);
    if (missingDocs) {
      return showMessage('error', t('operator.registerKit.step2.errors.docsRequired', {
        defaultValue: 'Please upload all 4 required documents before continuing.',
      }));
    }

    setSubmitting(true);
    setLoading(true);
    try {
      const registrationId = sessionStorage.getItem('registrationId');
      if (!registrationId) {
        throw new Error('Session lost');
      }
      await registerKitService.registerStep2({
        registrationId,
        fullName: formData.fullName.trim(),
        governmentIdType: formData.governmentIdType,
        idNumber: formData.idNumber.trim(),
        documents: documentUrls,
      });
      navigate('/operator/registerKitStep3');
    } catch (error: unknown) {
      const axiosErr2 = error instanceof AxiosError ? error as AxiosError<{ message?: string }> : null;
      const apiMsg = axiosErr2?.response?.data?.message;
      const statusCode = axiosErr2?.response?.status;
      let errorMsg = apiMsg || t('operator.registerKit.step2.errors.submitFailed', {
        defaultValue: 'Could not save your details. Please try again.',
      });
      if (statusCode === 401) {
        errorMsg = t('operator.registerKit.step2.errors.unauthorized', {
          defaultValue: 'Your session expired. Please sign in again.',
        });
        setTimeout(() => navigate('/signin'), 2000);
      }
      showMessage('error', errorMsg);
    } finally {
      setLoading(false);
      setTimeout(() => setSubmitting(false), 1000);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('operator.registerKit.step2.metaTitle', { defaultValue: 'Step 2: Identity Verification | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.registerKit.step2.metaDescription', {
            defaultValue: 'Verify your identity to complete your hotspot kit registration.',
          })}
        />
      </Helmet>
    
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-8 py-12">
          
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {message.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          <div className="mb-12">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
              {t('operator.registerKit.step2.heading', { defaultValue: 'Register Your Hotspot Kit' })}
            </h1>
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('operator.registerKit.step2.subtitle', { defaultValue: 'Step 2 of 3 • Identity Verification' })}
            </p>

            {/* Progress bar */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
                {t('operator.registerKit.progress.step1', { defaultValue: 'Step 1: Device' })}
              </span>
              <span className="text-sm font-bold text-[#2E5A88] uppercase tracking-wider">
                {t('operator.registerKit.progress.step2', { defaultValue: 'Step 2: Identity' })}
              </span>
              <span className={`text-sm font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>
                {t('operator.registerKit.progress.step3', { defaultValue: 'Step 3: Payment' })}
              </span>
            </div>
            <div className={`w-full rounded-full h-2 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'}`}>
              <div className="bg-[#2E5A88] h-2 rounded-full transition-all duration-500" style={{ width: '66%' }}></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Form Side */}
            <div className="space-y-6">
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <User className="text-[#2E5A88]" />
                {t('operator.registerKit.step2.sections.personalInfo', { defaultValue: 'Personal Information' })}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-bold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                    {t('operator.registerKit.step2.fields.fullName', { defaultValue: 'Full Name' })} <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder={t('operator.registerKit.step2.placeholders.fullName', { defaultValue: 'As shown on your ID' })}
                    className={`w-full p-4 rounded-xl border-2 bg-transparent focus:border-[#2E5A88] focus:ring-2 focus:ring-[#2E5A88]/20 outline-none transition-all ${theme === 'dark' ? 'border-gray-800 text-white placeholder-gray-600' : 'border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-bold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                    {t('operator.registerKit.step2.fields.idType', { defaultValue: 'ID Type' })} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="governmentIdType"
                    value={formData.governmentIdType}
                    onChange={handleInputChange}
                    className={`w-full p-4 rounded-xl border-2 bg-transparent focus:border-[#2E5A88] focus:ring-2 focus:ring-[#2E5A88]/20 outline-none ${theme === 'dark' ? 'border-gray-800 text-white' : 'border-gray-300 text-gray-900'}`}
                  >
                    <option value="">{t('operator.registerKit.step2.placeholders.idType', { defaultValue: 'Select ID Type' })}</option>
                    <option value="passport">{t('operator.registerKit.step2.idTypes.passport', { defaultValue: 'International Passport' })}</option>
                    <option value="national_id">{t('operator.registerKit.step2.idTypes.nationalId', { defaultValue: 'National ID Card' })}</option>
                    <option value="drivers_license">{t('operator.registerKit.step2.idTypes.driversLicense', { defaultValue: "Driver's License" })}</option>
                    <option value="voters_card">{t('operator.registerKit.step2.idTypes.votersCard', { defaultValue: "Voter's Card" })}</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-bold mb-2 uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                    {t('operator.registerKit.step2.fields.idNumber', { defaultValue: 'ID Number' })} <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    placeholder={t('operator.registerKit.step2.placeholders.idNumber', { defaultValue: 'Enter your ID number' })}
                    className={`w-full p-4 rounded-xl border-2 bg-transparent focus:border-[#2E5A88] focus:ring-2 focus:ring-[#2E5A88]/20 outline-none transition-all ${theme === 'dark' ? 'border-gray-800 text-white placeholder-gray-600' : 'border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  />
                </div>
              </div>
            </div>

            {/* Upload Side */}
            <div className="space-y-6">
              <div>
                <h2 className={`text-2xl font-bold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <FileText className="text-[#2E5A88]" />
                  {t('operator.registerKit.step2.sections.documents', { defaultValue: 'Identity Documents' })}
                </h2>
                <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                  {t('operator.registerKit.step2.docsHint', {
                    defaultValue: 'JPG, PNG, WebP or PDF. Max 5MB each.',
                  })}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FileUploadBox
                  label={t('operator.registerKit.step2.docs.idFront', { defaultValue: 'ID Front' })}
                  hint={t('operator.registerKit.step2.docHints.idFront', { defaultValue: 'Click to upload' })}
                  uploading={uploadingDoc === 'frontIdProof'}
                  onFileSelect={(f: File | null) => handleFileUpload('frontIdProof', f)}
                  hasFile={!!documentUrls.idFrontUrl}
                  theme={theme}
                />
                <FileUploadBox
                  label={t('operator.registerKit.step2.docs.idBack', { defaultValue: 'ID Back' })}
                  hint={t('operator.registerKit.step2.docHints.idBack', { defaultValue: 'Click to upload' })}
                  uploading={uploadingDoc === 'backIdProof'}
                  onFileSelect={(f: File | null) => handleFileUpload('backIdProof', f)}
                  hasFile={!!documentUrls.idBackUrl}
                  theme={theme}
                />
                <FileUploadBox
                  label={t('operator.registerKit.step2.docs.proofOfAddress', { defaultValue: 'Proof of Address' })}
                  hint={t('operator.registerKit.step2.docHints.address', { defaultValue: 'Utility bill, bank statement, etc.' })}
                  uploading={uploadingDoc === 'proofOfAddress'}
                  onFileSelect={(f: File | null) => handleFileUpload('proofOfAddress', f)}
                  hasFile={!!documentUrls.proofOfAddressUrl}
                  theme={theme}
                />
                <FileUploadBox
                  label={t('operator.registerKit.step2.docs.selfie', { defaultValue: 'Selfie with ID' })}
                  hint={t('operator.registerKit.step2.docHints.selfie', { defaultValue: 'Hold your ID next to your face' })}
                  uploading={uploadingDoc === 'selfieWithId'}
                  onFileSelect={(f: File | null) => handleFileUpload('selfieWithId', f)}
                  hasFile={!!documentUrls.selfieUrl}
                  theme={theme}
                />
              </div>
            </div>
          </div>

          <div className="mt-12 flex justify-between items-center">
            <button
              type="button"
              onClick={() => navigate('/operator/registerKit')}
              disabled={loading || submitting}
              className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                theme === 'dark'
                  ? 'text-gray-300 hover:bg-gray-800'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              {t('operator.registerKit.step2.back', { defaultValue: 'Back' })}
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading || submitting}
              className="px-12 py-4 bg-[#2E5A88] hover:bg-[#244a73] text-white font-bold rounded-xl transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('operator.registerKit.step2.submitting', { defaultValue: 'Saving...' })}
                </>
              ) : (
                <>
                  {t('operator.registerKit.step2.submit', { defaultValue: 'Continue to Payout' })}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

interface FileUploadBoxProps {
  label: string;
  hint: string;
  uploading: boolean;
  onFileSelect: (f: File | null) => void;
  hasFile: boolean;
  theme: 'light' | 'dark';
}

const FileUploadBox = ({ label, hint, uploading, onFileSelect, hasFile, theme }: FileUploadBoxProps) => (
  <div className={`relative p-6 border-2 border-dashed rounded-2xl transition-all flex flex-col items-center justify-center text-center gap-3 ${
    hasFile
      ? 'border-green-500 bg-green-50 dark:bg-green-500/5'
      : theme === 'dark'
        ? 'border-gray-800 bg-[#111] hover:border-gray-700'
        : 'border-gray-300 bg-white hover:border-[#2E5A88]'
  }`}>
    {hasFile ? <CheckCircle className="text-green-500" size={30} /> : <Upload className="text-[#2E5A88]" size={30} />}
    <div>
      <p className={`font-bold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{label}</p>
      <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
        {uploading ? 'Uploading...' : hasFile ? 'Uploaded' : (hint || 'Click to upload')}
      </p>
    </div>
    <input
      type="file"
      accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
      onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
      className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
      disabled={uploading}
    />
  </div>
);

export default HotspotRegistrationStep2;
